import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { BudgetListItem, SettlementListItem } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { budgetApi, settlementApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { MoneyGivenCombinedCard } from '../../components/settlement/employer/MoneyGivenCombinedCard';
import { resolveEmployerMoneyGivenCardView, settlementBelongsToBudget } from '../../utils/employerMoneyGivenSettlement';

const EMPLOYER_FINANCE_OPEN_RANGE_END_YMD = '9999-12-31';

export const EmployerSettlementPendingReviewPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [budgets, setBudgets] = useState<BudgetListItem[]>([]);
  const [settlements, setSettlements] = useState<SettlementListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fromPath = `${location.pathname}${location.search}`;

  const pendingBudgets = useMemo(() => {
    const submitted = settlements.filter(
      (row) => row.status === 'submitted' && row.isFromPreviousHelper !== true,
    );
    return budgets
      .filter(
        (budget) =>
          budget.isFromPreviousHelper !== true &&
          submitted.some((row) => settlementBelongsToBudget(row, budget)),
      )
      .sort((a, b) => b.startDate.localeCompare(a.startDate));
  }, [budgets, settlements]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [budgetRes, settlementMerged] = await Promise.all([
        budgetApi.list('all', 1, 100, '2000-01-01', EMPLOYER_FINANCE_OPEN_RANGE_END_YMD),
        (async () => {
          const pageSize = 100;
          const merged: SettlementListItem[] = [];
          for (let page = 1; page <= 20; page += 1) {
            const res = await settlementApi.list('all', page, pageSize);
            if (!res.success || !res.data) {
              break;
            }
            const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
            merged.push(...items);
            if (items.length < pageSize) {
              break;
            }
          }
          return merged;
        })(),
      ]);
      if (!budgetRes.success || !budgetRes.data) {
        setError(budgetRes.error ?? t('budget.loadError'));
        return;
      }
      setBudgets(Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data.items ?? []));
      setSettlements(settlementMerged);
    } catch {
      setError(t('settlement.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleBack = () => {
    navigateBack(navigate, '/settlements');
  };

  const handleNavigateDetail = (path: string) => {
    navigate(path, { state: { from: fromPath } });
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <EmployerTaskSubPageHeader
        title={t('settlement.pendingReviewPageTitle')}
        onBack={handleBack}
      />
      <main className={`mx-auto max-w-lg px-4 py-6 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : pendingBudgets.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('settlement.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-4" role="list">
            {pendingBudgets.map((row) => {
              const cardView = resolveEmployerMoneyGivenCardView(row, settlements);
              return (
                <li key={row.id}>
                  <MoneyGivenCombinedCard
                    row={row}
                    cardView={cardView}
                    settlements={settlements}
                    fromPath={fromPath}
                    periodLabel={t('settlement.period', { start: row.startDate, end: row.endDate })}
                    formatCreatedAtLabel={() =>
                      `${t('settlement.submittedAtLabel')}: ${cardView.activeSettlement?.submittedAt ?? '—'}`
                    }
                    formatSubItemCreatedAtLabel={(createdAt) =>
                      t('budget.timeLabel', { value: createdAt ?? '—' })
                    }
                    includesAddOnLabel={t('budget.mergedChildCount', {
                      count: row.mergedChildCount ?? 0,
                    })}
                    moneyGivenAmountLabel={t('settlement.budgetAmount')}
                    settlementResultLabel={t('settlement.balance')}
                    mainAmountLabel={t('budget.mainAmountLabel')}
                    subAmountLabel={t('budget.subAmountLabel')}
                    addOnLabel={t('budget.addOn')}
                    editAriaLabel={t('budget.editAria', { start: row.startDate, end: row.endDate })}
                    expandAriaLabel={t('budget.expandChildrenAria', {
                      start: row.startDate,
                      end: row.endDate,
                    })}
                    collapseAriaLabel={t('budget.collapseChildrenAria', {
                      start: row.startDate,
                      end: row.endDate,
                    })}
                    badgeUnsettledLabel={t('settlement.status.unsettled')}
                    badgePendingReviewLabel={t('settlement.status.submitted')}
                    badgeSettledLabel={t('settlement.status.settled')}
                    onNavigateDetail={handleNavigateDetail}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};
